// Coupons API Service
// Based on Zclub Kupon API Documentation
import AsyncStorage from '@react-native-async-storage/async-storage';
import { handleApiResponse } from './userDataService';
import { makeAuthenticatedApiCall, getSessionId, getRememberToken } from './authApi';

// Debug function to check session state
export const debugSessionState = async () => {
  try {
    const sessionId = await getSessionId();
    const rememberToken = await getRememberToken();
    const allKeys = await AsyncStorage.getAllKeys();
    const sessionKeys = allKeys.filter(key => key.includes('session') || key.includes('token') || key.includes('user'));
    
    console.log('=== COUPON SESSION DEBUG INFO ===');
    // console.log('Session ID:', sessionId);
    // console.log('Remember Token:', rememberToken ? 'Present' : 'Missing');
    // console.log('Relevant AsyncStorage keys:', sessionKeys);
    
    if (sessionKeys.length > 0) {
      const values = await AsyncStorage.multiGet(sessionKeys);
      // console.log('AsyncStorage values:', values);
    }
    
    return {
      sessionId,
      rememberToken,
      hasSession: !!sessionId,
      hasRememberToken: !!rememberToken,
      storageKeys: sessionKeys
    };
  } catch (error) {
    console.error('Error debugging session state:', error);
    return null;
  }
};

import { ZCLUB_API_URL, API_BASE_URL } from '../config/api';

const ZCLUB_BASE_URL = ZCLUB_API_URL;

// Test endpoint to verify API functionality
export const testCouponApi = async () => {
  try {
    const url = `${API_BASE_URL}/kupon/test`;
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    
    const result = await response.json();
    console.log('Coupon API test result:', result);
    return result;
  } catch (error) {
    console.error('Coupon API test failed:', error);
    throw new Error(error.message || 'Failed to test coupon API');
  }
};

// Get available coupons with pagination and filters
export const getCoupons = async (filters = {}) => {
  try {
    console.log('getCoupons called with filters:', filters);
    
    // Debug session state first
    await debugSessionState();
    
    // Build query parameters
    const params = new URLSearchParams();
    
    // Add pagination parameters
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());
    
    // Add filter parameters
    if (filters.kategori) params.append('kategori', filters.kategori);
    
    const queryString = params.toString();
    const url = `${ZCLUB_BASE_URL}/coupons${queryString ? `?${queryString}` : ''}`;
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    
    const result = await response.json();
    console.log('getCoupons API call successful');
    return result;
  } catch (error) {
    console.error('getCoupons API call failed:', error);
    throw new Error(error.message || 'Failed to fetch coupons');
  }
};

// Get coupon categories
export const getCouponCategories = async () => {
  try {
    console.log('getCouponCategories called');
    
    const url = `${ZCLUB_BASE_URL}/categories`;
    
    // console.log('Making API call to:', url);
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    
    const result = await response.json();
    console.log('getCouponCategories API call successful');
    return result;
  } catch (error) {
    console.error('getCouponCategories API call failed:', error);
    throw new Error(error.message || 'Failed to fetch coupon categories');
  }
};

// Claim a coupon (requires authentication)
export const claimCoupon = async (couponId, isIndobest = false) => {
  try {
    console.log('claimCoupon called with couponId:', couponId, 'isIndobest:', isIndobest);
    
    // Debug session state first
    await debugSessionState();
    
    const url = `${ZCLUB_BASE_URL}/claim-coupon`;
    
    // console.log('Making API call to:', url);
    
    // Use makeAuthenticatedApiCall for automatic session management
    const result = await makeAuthenticatedApiCall(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        coupon_id: couponId
      })
    });
    
    console.log('claimCoupon API call successful');
    return result;
  } catch (error) {
    console.error('claimCoupon API call failed:', error);

    // Handle Indobest-specific API errors (502 - Indobest API call failed)
    if (isIndobest && error.message && error.message.includes('502')) {
      throw new Error('Indobest API temporarily unavailable. Please try again later.');
    }

    throw error;
  }
};

// Get user's claimed coupons (requires authentication)
export const getMyCoupons = async (filters = {}) => {
  try {
    // console.log('getMyCoupons called with filters:', filters);
    console.log('getMyCoupons called');
    
    // Debug session state first
    await debugSessionState();
    
    // Build query parameters
    const params = new URLSearchParams();
    
    // Add pagination parameters
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());
    
    const queryString = params.toString();
    const url = `${ZCLUB_BASE_URL}/my-coupons${queryString ? `?${queryString}` : ''}`;
    
    // console.log('Making API call to:', url);
    
    // Use makeAuthenticatedApiCall for automatic session management
    const result = await makeAuthenticatedApiCall(url, {
      method: 'GET',
    });
    
    console.log('getMyCoupons API call successful');
    return result;
  } catch (error) {
    console.error('getMyCoupons API call failed:', error);
    throw new Error(error.message || 'Failed to fetch my coupons');
  }
};

// Get coupons with default filters for catalog
export const getCouponsCatalog = async (page = 1, limit = 10, kategori = '') => {
  return await getCoupons({
    page,
    limit,
    kategori,
  });
};

/**
 * React Native Image source from catalog-style coupon: uses coupon.image if set, else gambar_kupon URL.
 */
export const getCouponImageSource = (coupon) => {
  if (!coupon) return null;
  const img = coupon.image;
  if (img != null) {
    if (typeof img === 'number') return img;
    if (typeof img === 'object' && img.uri) return img;
  }
  const file = coupon.gambar_kupon;
  if (file == null || String(file).trim() === '') return null;
  const name = String(file).trim();
  const uri = /^https?:\/\//i.test(name) ? name : `${API_BASE_URL}/uploads/kupon/${name}`;
  return { uri };
};

// Helper function to format coupon data for display
export const formatCouponForDisplay = (coupon) => {
  // Convert string values to numbers to ensure proper comparison
  const remainingQuota = parseInt(coupon.kuota_tersisa) || 0;
  const totalQuota = parseInt(coupon.kuota_total) || 0;
  const minOrder = parseInt(coupon.minimal_order) || 0;
  const maxDiscount = parseInt(coupon.maksimal_diskon) || 0;
  const discountValue = parseFloat(coupon.nilai_diskon) || 0;
  const maxPerUser = parseInt(coupon.maksimal_per_user) || 0;
  const usageCount = parseInt(coupon.usage_count) || 0;
  const remainingUsage = parseInt(coupon.remaining_usage) || 0;
  
  // Indobest only — don't treat other external_id types (e.g. digiprosb) as Indobest
  const isIndobestCoupon = coupon.tipe_kupon === 'indobest';

  return {
    id: coupon.id,
    title: coupon.nama_kupon,
    code: coupon.kode_kupon,
    description: coupon.deskripsi,
    category: coupon.kategori,
    categoryName: coupon.nama_kategori,
    categoryIcon: coupon.ikon_kategori,
    type: coupon.tipe_kupon,
    tipe_kupon: coupon.tipe_kupon,
    discountValue: discountValue,
    maxDiscount: maxDiscount,
    minOrder: minOrder,
    totalQuota: totalQuota,
    remainingQuota: remainingQuota,
    maxPerUser: maxPerUser,
    usageCount: usageCount,
    remainingUsage: remainingUsage,
    image: getCouponImageSource(coupon),
    termsConditions: coupon.syarat_ketentuan,
    startDate: coupon.tanggal_mulai,
    endDate: coupon.tanggal_berakhir,
    isLimited: coupon.is_limited,
    is_claimed: coupon.is_claimed || false,
    isIndobest: isIndobestCoupon,
    externalId: coupon.external_id || null,
    availability: remainingQuota > 0 ? `Tersisa ${remainingQuota} kupon!` : 'Kuota habis',
    stockStatus: remainingQuota > 10 ? 'available' : remainingQuota > 0 ? 'limited' : 'out_of_stock',
    isEmptyCoupon: coupon.is_kupon_kosong,
    externalURL: coupon.link_tujuan,
  };
};

/** Build a navigable claimed-coupon object from POST /claim-coupon response. */
export const buildClaimedCouponFromClaimResponse = (response, fallbackCoupon = {}) => {
  const data = response?.data || {};
  const nested = data.coupon || {};
  const voucherDetails = data.voucher_details || {};
  const tipe = nested.tipe_kupon ?? fallbackCoupon.tipe_kupon ?? fallbackCoupon.type;
  const isPaygift = tipe === 'paygift';
  const isIndobest = tipe === 'indobest';
  const isDigitalReward = tipe === 'digiprosb';
  const voucherUrl = voucherDetails.voucher_url ?? data.voucher_url ?? null;
  const pendingReason =
    data.paygift_pending_reason ?? voucherDetails.paygift_pending_reason ?? null;
  const rewardSnRaw = voucherDetails.sn ?? data.reward_sn ?? null;
  const rewardSnReady = (() => {
    if (!rewardSnRaw) return false;
    const s = String(rewardSnRaw).trim();
    return !!s && s !== 'PENDING' && !s.startsWith('PENDING_');
  })();
  const rewardSn = rewardSnReady ? String(rewardSnRaw).trim() : rewardSnRaw;
  const rewardAwaitingPhone = isDigitalReward && !rewardSnReady
    ? !!(data.reward_awaiting_phone ?? voucherDetails.awaiting_phone)
    : false;
  const rewardFailed = isDigitalReward && !rewardSnReady
    ? !!(data.reward_failed ?? voucherDetails.failed)
    : false;
  const rewardProcessing = isDigitalReward && !rewardSnReady
    ? !!(data.reward_processing ?? voucherDetails.processing)
    : false;
  const rewardPending = isDigitalReward && !rewardSnReady
    ? !!(
        data.reward_pending
        ?? voucherDetails.pending
        ?? (rewardAwaitingPhone || rewardProcessing || rewardFailed || !rewardSn)
      )
    : false;
  const displayCode = isDigitalReward
    ? (rewardPending ? null : (rewardSn || data.coupon_code || null))
    : (data.coupon_code ?? nested.kode_kupon ?? fallbackCoupon.kode_kupon);

  return {
    ...fallbackCoupon,
    id: nested.id ?? fallbackCoupon.id,
    claim_id: data.claim_id,
    claimId: data.claim_id,
    kode_kupon: displayCode,
    code: displayCode,
    coupon_code: displayCode,
    nama_kupon: nested.nama_kupon ?? fallbackCoupon.nama_kupon ?? fallbackCoupon.title,
    title: nested.nama_kupon ?? fallbackCoupon.title ?? fallbackCoupon.nama_kupon,
    tipe_kupon: tipe,
    type: tipe,
    deskripsi: nested.deskripsi ?? fallbackCoupon.deskripsi,
    description: nested.deskripsi ?? fallbackCoupon.description ?? fallbackCoupon.deskripsi,
    syarat_ketentuan: nested.syarat_ketentuan ?? fallbackCoupon.syarat_ketentuan,
    tanggal_berakhir: nested.tanggal_berakhir ?? fallbackCoupon.tanggal_berakhir,
    endDate: nested.tanggal_berakhir ?? fallbackCoupon.endDate ?? fallbackCoupon.tanggal_berakhir,
    gambar_kupon: nested.gambar_kupon ?? fallbackCoupon.gambar_kupon,
    is_claimed: true,
    isPaygift,
    paygift: isPaygift,
    isIndobest,
    indobest: isIndobest,
    voucher_url: voucherUrl,
    voucher_expiry_date: voucherDetails.voucher_expiry_date ?? null,
    paygift_pending_reason: pendingReason,
    paygift_transaction_id: voucherDetails.paygift_transaction_id ?? null,
    external_ref_id: voucherDetails.external_ref_id ?? null,
    voucher_details: voucherDetails,
    reward_pending: rewardPending,
    reward_awaiting_phone: rewardAwaitingPhone,
    reward_processing: rewardProcessing,
    reward_failed: rewardFailed,
    reward_failure_message: data.reward_failure_message ?? voucherDetails.failure_message ?? null,
    reward_sn: rewardSn,
    suggested_phone: data.suggested_phone ?? null,
    tampil_barcode: !!(data.tampil_barcode ?? nested.tampil_barcode ?? fallbackCoupon.tampil_barcode),
    digiprosb_dest_type: data.digiprosb_dest_type ?? voucherDetails.dest_type ?? null,
    dest_form: data.dest_form ?? null,
  };
};

// Helper function to format claimed coupon data for display
export const formatClaimedCouponForDisplay = (claimedCoupon) => {
  const coupon = claimedCoupon.coupon;
  const tipe = coupon?.tipe_kupon;
  const rewardSnRaw = claimedCoupon.reward_sn ?? null;
  const rewardSnReady = (() => {
    if (!rewardSnRaw) return false;
    const s = String(rewardSnRaw).trim();
    return !!s && s !== 'PENDING' && !s.startsWith('PENDING_');
  })();
  const rewardSn = rewardSnReady ? String(rewardSnRaw).trim() : rewardSnRaw;
  const rewardPending = tipe === 'digiprosb' && rewardSnReady ? false : !!claimedCoupon.reward_pending;
  const rewardAwaitingPhone = tipe === 'digiprosb' && rewardSnReady ? false : !!claimedCoupon.reward_awaiting_phone;
  const rewardProcessing = tipe === 'digiprosb' && rewardSnReady ? false : !!claimedCoupon.reward_processing;
  const rewardFailed = tipe === 'digiprosb' && rewardSnReady ? false : !!claimedCoupon.reward_failed;
  let couponCode = claimedCoupon.coupon_code;
  if (tipe === 'digiprosb') {
    couponCode = rewardPending ? null : (rewardSn || claimedCoupon.coupon_code || null);
  }
  return {
    claimId: claimedCoupon.claim_id,
    claim_id: claimedCoupon.claim_id,
    couponCode,
    nama_batch: claimedCoupon.nama_batch || null,
    claimedAt: claimedCoupon.claimed_at,
    usedAt: claimedCoupon.used_at,
    orderId: claimedCoupon.order_id,
    discountUsed: claimedCoupon.discount_used,
    totalSpent: claimedCoupon.total_spent,
    status: claimedCoupon.status,
    ...formatCouponForDisplay(coupon),
    // Per-claim fields from my-coupons API root (not inside nested coupon)
    voucher_url: claimedCoupon.voucher_url ?? null,
    voucher_expiry_date: claimedCoupon.voucher_expiry_date ?? null,
    paygift: !!claimedCoupon.paygift,
    isPaygift: !!claimedCoupon.paygift,
    indobest: !!claimedCoupon.indobest,
    cashback: !!claimedCoupon.cashback,
    cashback_pending: !!claimedCoupon.cashback_pending,
    cashback_disbursed: !!claimedCoupon.cashback_disbursed,
    cashback_phone_display: claimedCoupon.cashback_phone_display ?? null,
    reference_no: claimedCoupon.reference_no ?? null,
    paygift_trx_id: claimedCoupon.paygift_trx_id ?? null,
    paygift_transaction_id:
      claimedCoupon.paygift_transaction_id ?? claimedCoupon.paygift_trx_id ?? null,
    paygift_ref_id: claimedCoupon.paygift_ref_id ?? null,
    external_ref_id: claimedCoupon.external_ref_id ?? null,
    paygift_pending_reason: claimedCoupon.paygift_pending_reason ?? null,
    reward_pending: rewardPending,
    reward_awaiting_phone: rewardAwaitingPhone,
    reward_processing: rewardProcessing,
    reward_failed: rewardFailed,
    reward_failure_message: claimedCoupon.reward_failure_message ?? null,
    reward_sn: rewardSn,
    suggested_phone: claimedCoupon.suggested_phone ?? null,
    tampil_barcode: !!(claimedCoupon.tampil_barcode ?? coupon?.tampil_barcode),
    digiprosb_dest_type: claimedCoupon.digiprosb_dest_type ?? null,
    dest_form: claimedCoupon.dest_form ?? null,
    validation_code:
      claimedCoupon.validation_code ?? claimedCoupon.external_ref_id ?? null,
    // Detail screens still expect catalog-style keys alongside formatted fields
    nama_kupon: coupon?.nama_kupon,
    deskripsi: coupon?.deskripsi,
    syarat_ketentuan: coupon?.syarat_ketentuan,
    tipe_kupon: coupon?.tipe_kupon,
    kode_kupon: couponCode ?? coupon?.kode_kupon,
    code: couponCode ?? coupon?.kode_kupon,
    gambar_kupon: coupon?.gambar_kupon,
    is_claimed: true,
    is_hexpod_reward: (coupon?.kegunaan_coupon || '') === 'hexpod_reward',
  };
};

// Helper function to format category data
export const formatCategoryForDisplay = (category) => {
  return {
    id: category.id,
    name: category.nama_kategori,
    description: category.deskripsi,
    icon: category.ikon_kategori,
    order: category.urutan_tampil,
  };
};

// Helper function to get discount text based on coupon type
export const getDiscountText = (coupon) => {
  const type = coupon.tipe_kupon || coupon.type;
  if (type === 'hadiah_produk') {
    return 'Hadiah Produk';
  }
  switch (type) {
    case 'diskon_persen':
      return `Diskon ${coupon.discountValue}%`;
    case 'diskon_nominal':
      return `Diskon Rp ${coupon.discountValue.toLocaleString('id-ID')}`;
    case 'gratis_ongkir':
      return 'Gratis Ongkir';
    case 'buy_one_get_one':
      return 'Buy 1 Get 1';
    case 'cashback': {
      const amount = Number(coupon.discountValue ?? coupon.nilai_diskon ?? 0);
      return amount > 0
        ? `Saldo LinkAja Rp ${amount.toLocaleString('id-ID')}`
        : 'Saldo LinkAja';
    }
    case 'digiprosb':
      return 'Voucher Digital';
    default:
      return 'Diskon Spesial';
  }
};

/** Pulsa/data-style reward issued on claim (tipe digiprosb). */
export const isDigitalRewardCoupon = (coupon) => {
  const type = coupon?.tipe_kupon || coupon?.type;
  return type === 'digiprosb';
};

export const isDigitalRewardAwaitingPhone = (coupon) => {
  if (!isDigitalRewardCoupon(coupon)) return false;
  // SN ready = done; never show destination form again
  if (getDigitalRewardSn(coupon)) return false;
  if (coupon?.reward_awaiting_phone || coupon?.voucher_details?.awaiting_phone) return true;
  // Legacy: pending without request_id / SN and not explicitly failed/processing
  if (coupon?.reward_failed || coupon?.reward_processing) return false;
  const reason = coupon?.paygift_pending_reason || '';
  if (reason === 'digiprosb_awaiting_phone') return true;
  if (reason === 'digiprosb_processing' || String(reason).startsWith('digiprosb_failed')) return false;
  // No ref yet → need phone
  const ref = coupon?.external_ref_id || coupon?.paygift_ref_id || coupon?.voucher_details?.external_ref_id;
  return !ref && !!coupon?.reward_pending;
};

export const isDigitalRewardFailed = (coupon) => {
  if (!isDigitalRewardCoupon(coupon)) return false;
  // SN ready = done; stale failed flags must not keep the retry form visible
  if (getDigitalRewardSn(coupon)) return false;
  if (coupon?.reward_failed || coupon?.voucher_details?.failed) return true;
  const reason = String(coupon?.paygift_pending_reason || '');
  return reason === 'digiprosb_failed' || reason.startsWith('digiprosb_failed:');
};

export const isDigitalRewardProcessing = (coupon) => {
  if (!isDigitalRewardCoupon(coupon)) return false;
  if (getDigitalRewardSn(coupon)) return false;
  if (isDigitalRewardAwaitingPhone(coupon) || isDigitalRewardFailed(coupon)) return false;
  if (coupon?.reward_processing || coupon?.voucher_details?.processing) return true;
  return !!coupon?.reward_pending || !!coupon?.voucher_details?.pending;
};

export const isDigitalRewardPending = (coupon) => {
  if (!isDigitalRewardCoupon(coupon)) return false;
  if (getDigitalRewardSn(coupon)) return false;
  return (
    isDigitalRewardAwaitingPhone(coupon)
    || isDigitalRewardProcessing(coupon)
    || isDigitalRewardFailed(coupon)
    || !!coupon?.reward_pending
    || !!coupon?.voucher_details?.pending
  );
};

export const getDigitalRewardSn = (coupon) => {
  const sn =
    coupon?.reward_sn ||
    coupon?.voucher_details?.sn ||
    coupon?.kode_kupon ||
    coupon?.code ||
    coupon?.coupon_code ||
    null;
  if (!sn) return null;
  const s = String(sn).trim();
  if (!s || s === 'PENDING' || s.startsWith('PENDING_')) return null;
  return s;
};

export const getDigitalRewardDestForm = (coupon) => {
  if (coupon?.dest_form && typeof coupon.dest_form === 'object') {
    return {
      type: coupon.dest_form.type || 'manual',
      label: coupon.dest_form.label || 'Data tujuan',
      placeholder: coupon.dest_form.placeholder || 'No. tujuan / lainnya',
      hint: coupon.dest_form.hint || '',
      submit_label: coupon.dest_form.submit_label || 'Proses Voucher',
      min_length: Number(coupon.dest_form.min_length) > 0 ? Number(coupon.dest_form.min_length) : 3,
      input_mode: coupon.dest_form.input_mode || 'text',
      use_profile: !!coupon.dest_form.use_profile,
    };
  }
  return {
    type: 'manual',
    label: 'Data tujuan',
    placeholder: 'No. tujuan / lainnya',
    hint: '',
    submit_label: 'Proses Voucher',
    min_length: 3,
    input_mode: 'text',
    use_profile: false,
  };
};

export const getDigitalRewardListStatusLabel = (coupon) => {
  if (!isDigitalRewardCoupon(coupon)) return null;
  if (getDigitalRewardSn(coupon)) return null;
  if (isDigitalRewardAwaitingPhone(coupon)) return 'Lengkapi data';
  if (isDigitalRewardFailed(coupon)) return 'Gagal — coba lagi';
  if (isDigitalRewardProcessing(coupon) || isDigitalRewardPending(coupon)) return 'Sedang diproses';
  return null;
};

/** Poll Digiprosb status for a pending digital-reward claim. */
export const refreshDigitalRewardClaim = async (claimId) => {
  const url = `${ZCLUB_BASE_URL}/refresh-digiprosb-claim`;
  const result = await makeAuthenticatedApiCall(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      claim_id: claimId,
      kuponDetailId: claimId,
    }),
  });
  if (result && (result.status === 'error' || result.code >= 400)) {
    throw new Error(result.message || 'Gagal memperbarui status voucher');
  }
  return result;
};

/** Submit destination to start/retry Digiprosb purchase for a claimed coupon. */
export const completeDigitalRewardClaim = async (claimId, destination) => {
  const url = `${ZCLUB_BASE_URL}/complete-digiprosb-claim`;
  const result = await makeAuthenticatedApiCall(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      claim_id: claimId,
      kuponDetailId: claimId,
      phone: destination,
      msisdn: destination,
      destination,
    }),
  });
  if (result && (result.status === 'error' || result.code >= 400)) {
    const err = new Error(result.message || 'Gagal memproses voucher');
    err.data = result.data || null;
    err.retry = !!result.retry;
    throw err;
  }
  return result;
};

/** Complete pending LinkAja cashback for a claimed Hexpod coupon. */
export const completeCashback = async (claimId, linkajaNumber) => {
  const url = `${ZCLUB_BASE_URL}/complete-cashback`;
  const result = await makeAuthenticatedApiCall(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      claim_id: claimId,
      linkaja_number: linkajaNumber,
    }),
  });
  if (result && (result.status === 'error' || result.success === false)) {
    throw new Error(result.message || 'Gagal memproses cashback LinkAja');
  }
  return result;
};

/** Hadiah produk Hexpod — verifikasi manual via kode unix */
export const isProductGiftCoupon = (coupon) => {
  const type = coupon?.tipe_kupon || coupon?.type;
  return type === 'hadiah_produk';
};

export const getHadiahProdukValidationCode = (coupon) => {
  return (
    coupon?.validation_code ||
    coupon?.external_ref_id ||
    coupon?.coupon?.validation_code ||
    null
  );
};

// Update voucher URL for paygift coupons (requires authentication)
export const updateCouponVoucherUrl = async (kuponDetailId) => {
  try {
    console.log('updateCouponVoucherUrl called with kuponDetailId:', kuponDetailId);
    
    // Debug session state first
    await debugSessionState();
    
    const url = `${ZCLUB_BASE_URL}/update-voucher-url`;
    
    // console.log('Making API call to:', url);
    
    // Use makeAuthenticatedApiCall for automatic session management
    // cache: 'no-store' ensures browser/proxy never reuse cached response
    const result = await makeAuthenticatedApiCall(url, {
      method: 'POST',
      cache: 'no-store',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        kuponDetailId: kuponDetailId
      })
    });
    
    console.log('updateCouponVoucherUrl API response:', result);
    
    if (result && result.success === false && result.pending === true) {
      return result;
    }

    if (result && result.success === false) {
      throw new Error(result.message || 'Failed to update voucher URL');
    }

    console.log('updateCouponVoucherUrl API call successful');
    return result;
  } catch (error) {
    console.error('updateCouponVoucherUrl API call failed:', error);
    throw new Error(error.message || 'Failed to update voucher URL');
  }
};

/** Deprecated: digest email replaced user report. Kept for older app builds. */
/** POST report-paygift-merchant-pending (needs PAYGIFT_MERCHANT_PENDING_EMAIL on server). */
export const reportPaygiftMerchantPending = async (kuponDetailId) => {
  const url = `${ZCLUB_BASE_URL}/report-paygift-merchant-pending`;
  const result = await makeAuthenticatedApiCall(url, {
    method: 'POST',
    cache: 'no-store',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ kuponDetailId }),
  });
  // resolved: voucher already ready — caller should refresh UI (not treat as hard error)
  if (result && result.success === false && result.resolved !== true) {
    throw new Error(result.message || 'Gagal mengirim laporan');
  }
  return result;
};

/** POST report-paygift-stock-shortage (Quantity shortage pending claims). */
export const reportPaygiftStockShortage = async (kuponDetailId) => {
  const url = `${ZCLUB_BASE_URL}/report-paygift-stock-shortage`;
  const result = await makeAuthenticatedApiCall(url, {
    method: 'POST',
    cache: 'no-store',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ kuponDetailId }),
  });
  if (result && result.success === false && result.resolved !== true) {
    throw new Error(result.message || 'Gagal mengirim laporan');
  }
  return result;
};

/** POST report-paygift-not-available (Not available pending claims). */
export const reportPaygiftNotAvailable = async (kuponDetailId) => {
  const url = `${ZCLUB_BASE_URL}/report-paygift-not-available`;
  const result = await makeAuthenticatedApiCall(url, {
    method: 'POST',
    cache: 'no-store',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ kuponDetailId }),
  });
  if (result && result.success === false && result.resolved !== true) {
    throw new Error(result.message || 'Gagal mengirim laporan');
  }
  return result;
};

/** POST report-paygift-api-down (maintenance/API down pending claims). */
export const reportPaygiftApiDown = async (kuponDetailId) => {
  const url = `${ZCLUB_BASE_URL}/report-paygift-api-down`;
  const result = await makeAuthenticatedApiCall(url, {
    method: 'POST',
    cache: 'no-store',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ kuponDetailId }),
  });
  if (result && result.success === false && result.resolved !== true) {
    throw new Error(result.message || 'Gagal mengirim laporan');
  }
  return result;
};

// Get user's claimed coupon by id (requires authentication)
export const getMyCouponById = async (couponId = 0) => {
  try {
    console.log('getMyCouponById called');
    
    // Debug session state first
    await debugSessionState();
    
    // Build query parameters
    const params = new URLSearchParams();
    params.append('id', couponId);
    
    const queryString = params.toString();
    const url = `${ZCLUB_BASE_URL}/coupon-by-id${queryString ? `?${queryString}` : ''}`;
    
    const result = await makeAuthenticatedApiCall(url, {
      method: 'GET',
    });
    
    console.log('getMyCouponById API call successful');
    return result;
  } catch (error) {
    console.error('getMyCouponById API call failed:', error);
    throw new Error(error.message || 'Failed to fetch my coupons');
  }
};

export default {
  testCouponApi,
  getCoupons,
  getCouponCategories,
  claimCoupon,
  getMyCoupons,
  getCouponsCatalog,
  updateCouponVoucherUrl,
  reportPaygiftMerchantPending,
  reportPaygiftStockShortage,
  reportPaygiftNotAvailable,
  reportPaygiftApiDown,
  completeCashback,
  formatCouponForDisplay,
  buildClaimedCouponFromClaimResponse,
  formatClaimedCouponForDisplay,
  formatCategoryForDisplay,
  getDiscountText,
};